import { getApiUrl, getToken, removeToken } from '$utils/asyncStorage';
import { getDeviceId } from '$utils/device';

export class PasskeyRedirect extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'PasskeyRedirect';
    this.code = code;
  }
}

export function apiMessage(value: unknown): string | null {
  if (value instanceof Error || !isRecord(value)) return null;
  if (typeof value.message !== 'string' || value.message === '') return null;
  return value.message;
}

export type PasskeySession = {
  accessToken: string;
  userId: number;
  isNew: boolean;
};

export function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

export function isCredential(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value) || value.type !== 'public-key') return false;
  if (typeof value.id !== 'string' || value.id === '') return false;
  return isRecord(value.response);
}

export function readCeremonyId(value: unknown): string | null {
  if (!isRecord(value)) return null;
  const ceremonyId = value.ceremonyId;
  if (typeof ceremonyId !== 'string' || ceremonyId === '') return null;
  return ceremonyId;
}

export function readRequestOptions(value: unknown): Record<string, unknown> | null {
  if (!isRecord(value)) return null;
  const options = value.optionsJSON;
  if (
    !isRecord(options) ||
    typeof options.challenge !== 'string' ||
    options.challenge === ''
  )
    return null;
  return options;
}

export function readCreationOptions(value: unknown): Record<string, unknown> | null {
  const options = readRequestOptions(value);
  if (!options || !isRecord(options.rp) || !isRecord(options.user)) return null;
  if (!Array.isArray(options.pubKeyCredParams) || options.pubKeyCredParams.length === 0)
    return null;
  return options;
}

export function readSession(value: unknown): PasskeySession | null {
  if (!isRecord(value)) return null;
  const accessToken = value.access_token;
  const userId = value.user_id;
  const isNew = value.is_new;
  if (typeof accessToken !== 'string' || accessToken === '') return null;
  if (typeof userId !== 'number' || !Number.isInteger(userId) || userId <= 0) return null;
  if (typeof isNew !== 'boolean') return null;
  return { accessToken, userId, isNew };
}

export type PasskeyCredential = {
  id: string;
  name: string;
};

export function readCredentials(value: unknown): PasskeyCredential[] | null {
  if (!isRecord(value) || !Array.isArray(value.credentials)) return null;
  const credentials: PasskeyCredential[] = [];
  for (const item of value.credentials) {
    if (!isRecord(item) || typeof item.id !== 'string' || item.id === '') continue;
    credentials.push({
      id: item.id,
      name: typeof item.name === 'string' ? item.name : '',
    });
  }
  return credentials;
}

export function readSuccess(value: unknown): boolean {
  return isRecord(value) && value.success === true;
}

function authorizationToken(): string {
  const stored = getToken();
  if (!stored || typeof stored.token !== 'string') return '';
  return stored.token;
}

function deviceID(): string {
  const id = getDeviceId();
  if (typeof id !== 'string') return '';
  return id;
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

export async function postJSON(
  path: string,
  body: Record<string, unknown>,
  headers: Record<string, string> = {},
): Promise<unknown> {
  const base = getApiUrl();
  if (typeof base !== 'string' || base === '') throw new Error('passkey');
  const response = await fetch(`${base.replace(/\/$/, '')}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authorizationToken(),
      ...headers,
    },
    body: JSON.stringify(body),
  });
  const data = await readBody(response);
  if (!response.ok) {
    const redirected = passkeyRedirect(data);
    if (redirected) throw redirected;
    throw data;
  }
  return data;
}

function passkeyRedirect(data: unknown): PasskeyRedirect | null {
  if (!isRecord(data) || typeof data.code !== 'string') return null;
  const message = typeof data.message === 'string' ? data.message : '';
  if (data.code === 'blocked') {
    removeToken();
    location.href = '/blocked';
    return new PasskeyRedirect('blocked', message);
  }
  if (data.code === 'country_blocked') {
    location.href = '/country_blocked';
    return new PasskeyRedirect('country_blocked', message);
  }
  return null;
}

export function authenticationBody(
  ceremonyId: string,
  credential: Record<string, unknown>,
): Record<string, unknown> {
  return { ceremonyId, credential, device: deviceID() };
}

export function credentialBody(
  ceremonyId: string,
  credential: Record<string, unknown>,
): Record<string, unknown> {
  return { ceremonyId, credential };
}
